import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";

import { ErrorBoundary } from "@sentry/react";
import { useLocation } from "react-router";

import AnnouncementPageId from "@mapmycustomers/shared/enum/announcements/AnnouncementPageId";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import {
  fetchAnnouncements,
  hideAnnouncements,
  openAnnouncements,
  skipAnnouncements,
  triggerAnnouncements,
} from "@app/store/announcements/actions";
import {
  getAnnouncementsPendingTrigger,
  getEligibleAnnouncements,
  isAnnouncementsHiddenForSession,
  isAnnouncementsLoaded,
  isAnnouncementsLoading,
  isAnnouncementsModalOpen,
  isAnnouncementsStartupWaitOver,
} from "@app/store/announcements/selectors";
import { getMe } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import getAnnouncementsGateAction from "@app/util/announcements/getAnnouncementsGateAction";
import useAnnouncementPageId from "@app/util/announcements/useAnnouncementPageId";
import { ANNOUNCEMENTS_GUIDE_ID } from "@app/util/contexts/GuidesContext";
import useGuides from "@app/util/contexts/useGuides";

import Announcements from "../Announcements";

import useAnnouncementsPageArrival from "./useAnnouncementsPageArrival";
import useAnnouncementsSlot from "./useAnnouncementsSlot";

interface AnnouncementsGateProps {
  pageId: AnnouncementPageId | undefined;
}

const AnnouncementsGate = ({
  eligible,
  hiddenForSession,
  loaded,
  loading,
  onFetch,
  onHide,
  onOpen,
  onSkip,
  onTrigger,
  open,
  pageId,
  pendingTrigger,
  signedIn,
  startupWaitOver,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps & AnnouncementsGateProps) => {
  const showPending = pendingTrigger !== undefined;
  const { visibility } = useGuides();
  const { acquire, release } = useAnnouncementsSlot(open, signedIn);
  const requestedRef = useRef(false);

  // A fallback for a session whose startup sent no request; the idle wait keeps it off interactive.
  useEffect(() => {
    if (!signedIn) {
      // A different user may sign in in this same tab; let them get their own first fetch.
      requestedRef.current = false;
      return;
    }
    // The startup request, a failed one or a Layout remount leaves nothing to fetch here.
    if (requestedRef.current || hiddenForSession || loaded || loading || startupWaitOver) {
      return;
    }
    requestedRef.current = true;
    const idle = window.requestIdleCallback?.(() => onFetch());
    const timer = idle === undefined ? window.setTimeout(() => onFetch(), 0) : undefined;
    return () => {
      if (idle !== undefined) {
        window.cancelIdleCallback?.(idle);
      }
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [hiddenForSession, loaded, loading, onFetch, signedIn, startupWaitOver]);

  // One refetch when the tab becomes visible again, never while the modal is open or announcements are hidden.
  useEffect(() => {
    if (!signedIn || open || hiddenForSession) {
      return;
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        onFetch();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [hiddenForSession, onFetch, open, signedIn]);

  const isAnyOtherGuideVisible = Object.entries(visibility).some(
    ([guideId, visible]) => visible && guideId !== ANNOUNCEMENTS_GUIDE_ID
  );
  const gateState = useMemo(
    () => ({
      eligibleCount: eligible.length,
      hiddenForSession,
      isAnotherGuideVisible: isAnyOtherGuideVisible,
      open,
      signedIn,
    }),
    [eligible.length, hiddenForSession, isAnyOtherGuideVisible, open, signedIn]
  );

  // Decides once the new page has settled; handleWait keeps guides off until then.
  const handleArrival = useDynamicCallback(() => {
    const action = getAnnouncementsGateAction({
      ...gateState,
      isSettling: false,
      showPending: true,
    });
    if (action === "open" && acquire()) {
      // A startup trigger that waited for the page to settle keeps its name.
      onOpen({ pageId, queue: eligible, trigger: pendingTrigger ?? "navigation" });
      return;
    }
    release();
    onTrigger();
  });

  // While a new page settles, hold the slot only if the modal would open there.
  const handleWait = useDynamicCallback(() => {
    // A guide on screen makes acquire() refuse, so only the modal's own conditions decide here.
    const wouldOpen =
      getAnnouncementsGateAction({
        ...gateState,
        isAnotherGuideVisible: false,
        isSettling: false,
        showPending: true,
      }) === "open";
    if (wouldOpen) {
      acquire();
    } else {
      release();
    }
  });

  const { pathname } = useLocation();
  const settled = useAnnouncementsPageArrival(pageId, pathname, handleArrival, handleWait);

  // Holds the slot while the page settles, then opens a pending trigger; a guide on screen keeps the
  // trigger waiting. A layout effect: it runs before every guide's passive effect.
  useLayoutEffect(() => {
    const action = getAnnouncementsGateAction({ ...gateState, isSettling: !settled, showPending });
    if (action === "hold") {
      acquire();
    } else if (action === "open") {
      // A pending request always carries its trigger; the check narrows the type.
      if (pendingTrigger && acquire()) {
        onOpen({ pageId, queue: eligible, trigger: pendingTrigger });
      }
    } else if (action === "skip") {
      release();
      onSkip();
    }
  }, [
    acquire,
    eligible,
    gateState,
    onOpen,
    onSkip,
    pageId,
    pendingTrigger,
    release,
    settled,
    showPending,
  ]);

  const handleRenderError = useCallback(() => onHide(), [onHide]);

  if (!signedIn || !open) {
    return null;
  }

  // A render error shows nothing and hides announcements for the session, releasing the guides.
  return (
    <ErrorBoundary fallback={<></>} onError={handleRenderError}>
      <ExtendedAnalytics keyPath="Announcements">
        <Announcements />
      </ExtendedAnalytics>
    </ErrorBoundary>
  );
};

const mapStateToProps = (state: RootState, { pageId }: AnnouncementsGateProps) => ({
  eligible: getEligibleAnnouncements(state, pageId),
  hiddenForSession: isAnnouncementsHiddenForSession(state),
  loaded: isAnnouncementsLoaded(state),
  loading: isAnnouncementsLoading(state),
  open: isAnnouncementsModalOpen(state),
  pendingTrigger: getAnnouncementsPendingTrigger(state),
  signedIn: Boolean(getMe(state)),
  startupWaitOver: isAnnouncementsStartupWaitOver(state),
});

const mapDispatchToProps = {
  onFetch: fetchAnnouncements.request,
  onHide: hideAnnouncements,
  onOpen: openAnnouncements,
  onSkip: skipAnnouncements,
  onTrigger: triggerAnnouncements,
};

const ConnectedAnnouncementsGate = connect(mapStateToProps, mapDispatchToProps)(AnnouncementsGate);

// The page comes from the router context, so the gate follows the route React has rendered.
const AnnouncementsGateForCurrentPage = () => {
  const pageId = useAnnouncementPageId();
  return <ConnectedAnnouncementsGate pageId={pageId} />;
};

export default AnnouncementsGateForCurrentPage;
